export const RADIO_LIST_INPUT_TYPE = "radio-list";
export const CHECKBOX_LIST_INPUT_TYPE = "checkbox-list";

export const normalizeRadioListInputType = (type) =>
  String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");

export const isCheckboxListInputType = (type) =>
  normalizeRadioListInputType(type) === CHECKBOX_LIST_INPUT_TYPE;

export const isRadioListInputType = (type) => {
  const key = normalizeRadioListInputType(type);
  return key === RADIO_LIST_INPUT_TYPE || key === CHECKBOX_LIST_INPUT_TYPE;
};

export const getRadioListTypeLabel = (type) =>
  isCheckboxListInputType(type) ? "Checkbox List" : "Radio List";

export const parseRadioListLabels = (raw) => {
  if (Array.isArray(raw)) {
    if (
      raw.length === 1 &&
      typeof raw[0] === "string" &&
      /\r|\n/.test(raw[0])
    ) {
      return parseRadioListLabels(raw[0]);
    }

    return raw.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof raw !== "string") {
    return [];
  }

  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
};

export const RADIO_LIST_ROWS_MARKER = "---UNZIPPD_RADIO_LIST_ROWS---";

export const joinRadioListEditorLines = (raw) =>
  parseRadioListLabels(raw).join("\n");

const tryParseJson = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();

  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const splitPackedLabels = (labels = []) => {
  const idx = labels.indexOf(RADIO_LIST_ROWS_MARKER);

  if (idx === -1) {
    return null;
  }

  return {
    headers: labels.slice(0, idx),
    rows: labels.slice(idx + 1),
  };
};

const labelsFromNested = (nested) => {
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) {
    return null;
  }

  const headers = parseRadioListLabels(
    nested.headers ?? nested.header_titles ?? nested.input_options,
  );
  const rows = parseRadioListLabels(
    nested.rows ?? nested.row_options ?? nested.row_titles ?? nested.rowOptions,
  );

  if (!headers.length && !rows.length) {
    return null;
  }

  return { headers, rows };
};

export const extractRadioListConfig = (input = {}) => {
  const nested =
    labelsFromNested(input.radio_list) ||
    labelsFromNested(input.radioList) ||
    labelsFromNested(input.radio_list_config) ||
    labelsFromNested(
      input.input_options &&
        typeof input.input_options === "object" &&
        !Array.isArray(input.input_options)
        ? input.input_options
        : null,
    );

  if (nested?.headers.length && nested?.rows.length) {
    return nested;
  }

  if (Array.isArray(input.input_options) && input.input_options.length === 1) {
    const parsed = labelsFromNested(tryParseJson(input.input_options[0]));
    if (parsed?.headers.length && parsed?.rows.length) {
      return parsed;
    }
  }

  if (typeof input.input_options === "string") {
    const parsed = labelsFromNested(tryParseJson(input.input_options));
    if (parsed?.headers.length && parsed?.rows.length) {
      return parsed;
    }
  }

  const optionLabels = parseRadioListLabels(input.input_options);
  const packed =
    splitPackedLabels(optionLabels) ||
    splitPackedLabels(parseRadioListLabels(input.options));

  const headers = parseRadioListLabels(
    packed?.headers ??
      input.header_options ??
      input.headers ??
      input.header_titles ??
      (packed ? null : input.input_options) ??
      input.options,
  );

  const rows = parseRadioListLabels(
    input.row_options ??
      input.rowOptions ??
      input.row_titles ??
      input.input_rows ??
      input.left_options ??
      input.left_column_titles ??
      packed?.rows,
  );

  if (packed && (!headers.length || !rows.length)) {
    return {
      headers: packed.headers,
      rows: rows.length ? rows : packed.rows,
    };
  }

  if (nested) {
    return {
      headers: headers.length ? headers : nested.headers,
      rows: rows.length ? rows : nested.rows,
    };
  }

  return { headers, rows };
};

export const buildRadioListResponseFields = (input = {}) => {
  const fromEditorHeaders = parseRadioListLabels(input.options);
  const fromEditorRows = parseRadioListLabels(input.rowOptions);
  const extracted = extractRadioListConfig(input);
  const headers = fromEditorHeaders.length
    ? fromEditorHeaders
    : extracted.headers;
  const rows = fromEditorRows.length ? fromEditorRows : extracted.rows;

  return {
    input_options: [...headers, RADIO_LIST_ROWS_MARKER, ...rows],
    row_options: rows,
  };
};

export const getRadioListEditorState = (input = {}) => {
  const { headers, rows } = extractRadioListConfig(input);

  return {
    options: joinRadioListEditorLines(headers),
    rowOptions: joinRadioListEditorLines(rows),
  };
};

export const getRadioListHeaders = (input = {}) =>
  extractRadioListConfig(input).headers;

export const getRadioListRows = (input = {}) =>
  extractRadioListConfig(input).rows;

export const formatRadioListEntry = (rowTitle, headerTitle) =>
  `${String(rowTitle ?? "").trim()}: ${String(headerTitle ?? "").trim()}`;

export const parseRadioListEntry = (entry) => {
  const text = String(entry ?? "").trim();

  if (!text) {
    return null;
  }

  const separatorIndex = text.indexOf(": ");

  if (separatorIndex <= 0) {
    return null;
  }

  const row = text.slice(0, separatorIndex).trim();
  const header = text.slice(separatorIndex + 2).trim();

  if (!row || !header) {
    return null;
  }

  return { row, header };
};

const toHeaderList = (header) => {
  if (Array.isArray(header)) {
    return header.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof header === "string" || typeof header === "number") {
    const text = String(header).trim();
    return text ? [text] : [];
  }

  return [];
};

export const parseRadioListSelections = (value, { multiple = false } = {}) => {
  const selections = {};

  if (value == null || value === "") {
    return selections;
  }

  const assign = (row, header) => {
    const rowTitle = String(row ?? "").trim();
    const headerTitles = toHeaderList(header);

    if (!rowTitle || headerTitles.length === 0) {
      return;
    }

    if (multiple) {
      const current = Array.isArray(selections[rowTitle])
        ? selections[rowTitle]
        : [];
      headerTitles.forEach((headerTitle) => {
        if (!current.includes(headerTitle)) {
          current.push(headerTitle);
        }
      });
      selections[rowTitle] = current;
      return;
    }

    selections[rowTitle] = headerTitles[headerTitles.length - 1];
  };

  if (Array.isArray(value)) {
    value.forEach((entry) => {
      if (typeof entry === "string") {
        const parsed = parseRadioListEntry(entry);
        if (parsed) {
          assign(parsed.row, parsed.header);
        }
        return;
      }

      if (entry && typeof entry === "object") {
        assign(
          entry.row ?? entry.row_title ?? entry.left,
          entry.headers ??
            entry.header ??
            entry.header_title ??
            entry.value,
        );
      }
    });

    return selections;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([row, header]) => {
      assign(row, header);
    });

    return selections;
  }

  if (typeof value === "string") {
    const parsed = parseRadioListEntry(value);
    if (parsed) {
      assign(parsed.row, parsed.header);
    }
  }

  return selections;
};

export const serializeRadioListSelections = (
  selections = {},
  rows = [],
  { multiple = false } = {},
) => {
  const orderedRows = rows.length > 0 ? rows : Object.keys(selections);

  return orderedRows.flatMap((row) => {
    const headers = multiple
      ? toHeaderList(selections[row])
      : toHeaderList(selections[row]).slice(-1);

    return headers.map((header) => formatRadioListEntry(row, header));
  });
};

export const getRadioListDisplayLines = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry ?? "").trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed) {
      return [];
    }

    if (/\r?\n/.test(trimmed)) {
      return trimmed
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    }

    if (trimmed.includes("; ")) {
      return trimmed
        .split("; ")
        .map((line) => line.trim())
        .filter(Boolean);
    }

    return [trimmed];
  }

  return serializeRadioListSelections(
    parseRadioListSelections(value, { multiple: true }),
    [],
    { multiple: true },
  );
};

export const formatRadioListDisplayValue = (value, separator = "; ") =>
  getRadioListDisplayLines(value).join(separator);

export const isRadioListResponseComplete = (value, input) => {
  const rows = getRadioListRows(input);
  if (rows.length === 0) {
    return false;
  }

  const multiple = isCheckboxListInputType(input?.input_type || input?.type);
  const selections = parseRadioListSelections(value, { multiple });

  return rows.every((row) =>
    multiple ? toHeaderList(selections[row]).length > 0 : Boolean(selections[row]),
  );
};

export const isRadioListRequiredEmpty = (input, value) =>
  isRadioListInputType(input?.input_type || input?.type) &&
  !isRadioListResponseComplete(value, input);

export const getRadioListSetupError = ({
  headers,
  rows,
  inputTitle = "Radio List",
} = {}) => {
  const title = String(inputTitle || "Radio List").trim() || "Radio List";
  const headerLabels = parseRadioListLabels(headers);
  const rowLabels = parseRadioListLabels(rows);

  if (headerLabels.length === 0) {
    return `${title}: add at least one header title.`;
  }

  if (rowLabels.length === 0) {
    return `${title}: add at least one left column title.`;
  }

  return null;
};
